// Where the Temp and Vent tabs get their data: the server (the /data/*.json exports, /energy/plan,
// /ventilation/state), or generated mock data with `?mock=temp` / `?mock=vent`. Variants: `-old` = what older
// exports / controllers / vent.js send (none of the optional fields), `vent-override` = a manual level
// override, `vent-bypass` = bypass open (no preheat), `vent-stopped` = the supply fan stopped.
// `&at=2026-01-13T14:20:00%2B02:00` pins the mock clock (screenshots against the prototypes).
import { useMemo } from 'react';

import { EnergyPlanSnapshot, VentilationState } from '../../common';
import { energyMockSnapshot, legacySnapshot } from './energy_mock';
import { RrdFile, isRrdFile } from './rrd';
import { mockAirQuality, tempMockFile } from './temp_mock';
import { ventMockFile, ventMockState } from './vent_mock';

export interface HomeData {
  mock: boolean;
  now(): number;
  rrd(name: string): Promise<RrdFile | undefined>; // undefined: file missing or not an rrd export
  plan(): Promise<EnergyPlanSnapshot | undefined>;
  vent(): Promise<VentilationState | undefined>;
}

async function fetchJson(url: string): Promise<unknown> {
  try {
    const res = await fetch(url);
    return res.ok ? await res.json() : undefined;
  } catch (e) {
    console.log(`ERROR: ${url}`);
    console.log(e);
    return undefined;
  }
}

const serverData: HomeData = {
  mock: false,
  now: () => Date.now(),
  async rrd(name) {
    const file = await fetchJson(`data/${name}.json`);
    return isRrdFile(file) ? file : undefined;
  },
  async plan() {
    const plan = (await fetchJson('/energy/plan')) as EnergyPlanSnapshot | undefined;
    return Array.isArray(plan?.slots) ? plan : undefined;
  },
  async vent() {
    const state = (await fetchJson('/ventilation/state')) as VentilationState | undefined;
    return state?.temperatures === undefined ? undefined : state;
  },
};

type MockVariant = 'old' | 'override' | 'bypass' | 'stopped' | undefined;

function mockData(variant: MockVariant, clockOffsetMs: number): HomeData {
  const legacy = variant === 'old';
  const now = () => Date.now() + clockOffsetMs;
  return {
    mock: true,
    now,
    async rrd(name) {
      return tempMockFile(name, now(), legacy) ?? ventMockFile(name, now(), legacy);
    },
    async plan() {
      const t = now();
      const s: EnergyPlanSnapshot = {
        ...energyMockSnapshot(t),
        ventilationMode: 'live',
        airQuality: mockAirQuality(t),
        ventManualOverrideSince: variant === 'override' ? t - 20 * 60_000 : undefined,
      };
      if (!legacy) {
        return s;
      }
      const old = legacySnapshot(s);
      return {
        ...old,
        slots: old.slots.map(({ ventLevel, ...slot }) => slot),
        actual: old.actual.map(({ ventLevel, ...slot }) => slot),
      };
    },
    async vent() {
      const state = ventMockState(now(), legacy);
      if (variant === 'bypass') {
        return { ...state, bypassPercentage: 40, preheat: false };
      }
      return variant === 'stopped' ? { ...state, supplyFanRunning: false } : state;
    },
  };
}

function homeDataFromUrl(): HomeData {
  const params = new URLSearchParams(window.location.search);
  const mock = params.get('mock') ?? '';
  const m = mock.match(/^(temp|vent)(?:-(old|override|bypass|stopped))?$/);
  if (!m) {
    return serverData;
  }
  const at = Date.parse(params.get('at') ?? '');
  return mockData(m[2] as MockVariant, Number.isNaN(at) ? 0 : at - Date.now());
}

export const useHomeData = () => useMemo(homeDataFromUrl, []);
